import { theme } from "@tm/utility/global";
import { ORDER_DETAIL_V2_ASSETS } from "../constants";
import type {
  Entity,
  HeroComponentData,
  OrderDetailV2ContextValue,
} from "../types";
import type {
  ApiAction,
  ApiBookingInfoData,
  ApiContactDetailData,
  ApiEndorsementNudgeData,
  ApiEntityDetailData,
  ApiNomineeDetailData,
  ApiOrderActionsData,
  ApiOrderData,
  ApiOrderDetailsRefundTrackerData,
  ApiPaymentInfoData,
  ApiPlanBenefitsData,
  ApiPolicyDownloadData,
  ApiPriceBreakupData,
  ApiPriceBreakupItem,
  ApiPricePaymentInfoData,
  ApiTopStatusCardData,
} from "../types/api";

const safeText = (value: unknown, fallback = "") =>
  value === undefined || value === null || value === ""
    ? fallback
    : String(value);

/**
 * Helper to handle boolean values that may be omitted from JSON (omitEmpty tag).
 * If the value is present (not undefined), use it. Otherwise, default to false.
 */
const getBooleanValue = (value: boolean | undefined): boolean => {
  return value !== undefined ? value : false;
};

/**
 * Shared helper to extract and sanitise price breakup data.
 * Used by both PRICE_BREAKUP and PRICE_PAYMENT_INFO transformers.
 */
const extractPriceBreakup = (priceBreakupData: {
  breakupList?: ApiPriceBreakupItem[];
  extraCharges?: ApiPriceBreakupItem[];
  finalAmount?: ApiPriceBreakupItem[] | ApiPriceBreakupItem;
}) => {
  const rawBreakupList = Array.isArray(priceBreakupData?.breakupList)
    ? priceBreakupData.breakupList
    : [];

  const breakupList = rawBreakupList
    .filter((item) => item && (item.label || item.value))
    .map((item) => ({
      label: safeText(item?.label, ""),
      value: safeText(item?.value, ""),
      style: item?.style && typeof item.style === "object" ? item.style : {},
    }));

  const rawExtraCharges = Array.isArray(priceBreakupData?.extraCharges)
    ? priceBreakupData.extraCharges
    : [];

  const extraCharges = rawExtraCharges
    .filter((item) => item && (item.label || item.value))
    .map((item) => ({
      label: safeText(item?.label, ""),
      value: safeText(item?.value, ""),
      style: item?.style && typeof item.style === "object" ? item.style : {},
    }));

  let finalAmount:
    | { label: string; value: string; style: Record<string, unknown> }
    | undefined;

  if (priceBreakupData?.finalAmount) {
    if (Array.isArray(priceBreakupData.finalAmount)) {
      const firstItem = priceBreakupData.finalAmount[0];
      if (firstItem && (firstItem.label || firstItem.value)) {
        finalAmount = {
          label: safeText(firstItem?.label, ""),
          value: safeText(firstItem?.value, ""),
          style:
            firstItem?.style && typeof firstItem.style === "object"
              ? firstItem.style
              : {},
        };
      }
    } else if (typeof priceBreakupData.finalAmount === "object") {
      const item = priceBreakupData.finalAmount;
      if (item && (item.label || item.value)) {
        finalAmount = {
          label: safeText(item?.label, ""),
          value: safeText(item?.value, ""),
          style:
            item?.style && typeof item.style === "object" ? item.style : {},
        };
      }
    }
  }

  return { breakupList, extraCharges, finalAmount };
};

const mapActions = (raw?: ApiAction[]) =>
  Array.isArray(raw)
    ? raw.map((a) => ({
        actionId: safeText(a?.actionId),
        label: safeText(a?.label),
        isEnabled: getBooleanValue(a?.isEnabled),
        isVisible: getBooleanValue(a?.isVisible),
      }))
    : [];

/**
 * Safely extract the entities array from context.
 */
const getEntities = (ctx: OrderDetailV2ContextValue): Entity[] =>
  Array.isArray(ctx.pageData?.entities)
    ? (ctx.pageData.entities as Entity[])
    : [];

/**
 * A transformer converts raw API component data into props for a React component.
 * Receives the component's raw `data` blob and the current context value.
 */
export type ComponentTransformerFn = (
  apiData: unknown,
  ctx: OrderDetailV2ContextValue
) => Record<string, unknown> | null;

export const COMPONENT_TRANSFORMER: Record<string, ComponentTransformerFn> = {
  COMPONENT_ORDER_DETAILS_PAGE_TITLE: (apiData, ctx) => {
    const hero = (apiData as HeroComponentData) || {};
    const header = hero?.header;
    if (!header) return null;

    const style = header?.style || {};
    const bgColor = Array.isArray(style?.backgroundColor)
      ? style.backgroundColor[0]
      : style?.backgroundColor;

    return {
      title: header?.title,
      orderIdLabel: header?.subTitle,
      icon: header?.icon,
      backgroundImage: style?.backgroundImage,
      bgColor,
      bookingId: ctx.bookingId,
      showNav: ctx.showNav,
      headerBackgroundColor: Array.isArray(style?.headerBackgroundColor)
        ? style.headerBackgroundColor
        : [],
      persuasionText: header?.persuasionText || [],
      persuasionSubTitle: header?.persuasionSubTitle || [],
      action: header?.action || [],
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_ORDER_DATA: (apiData) => {
    const orderData = (apiData as ApiOrderData) || {};
    const header = orderData?.header || {};
    return {
      insPlanName: safeText(header?.title),
      insuranceInfo: header?.subTitle
        ? { text: { value: safeText(header.subTitle) } }
        : undefined,
      insuranceType: undefined,
      orderDetailIcon: safeText(
        header?.icon,
        ORDER_DETAIL_V2_ASSETS.TRIP_SECURED_ICON
      ),
      orderDetails: Array.isArray(orderData?.orderDetailsList)
        ? orderData.orderDetailsList
        : [],
      separatorColor: header?.backgroundColor || undefined,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_BOOKING_INFO: (apiData) => {
    const bookingInfo = (apiData as ApiBookingInfoData) || {};
    const header = bookingInfo?.header || {};
    const orderDetailsList = Array.isArray(bookingInfo?.orderDetailsList)
      ? bookingInfo.orderDetailsList
      : [];
    const actionsList = Array.isArray(bookingInfo?.actionsList)
      ? bookingInfo.actionsList
      : [];

    const orderDetails = orderDetailsList.map((item) => ({
      label: safeText(item?.label),
      value: safeText(item?.value),
    }));

    const actionConfigs = bookingInfo?.actionsConfig || {};

    const items = actionsList
      .filter((a) => getBooleanValue(a?.isVisible))
      .map((a) => {
        const disabled = !getBooleanValue(a?.isEnabled);
        return {
          title: safeText(a?.label),
          icon: safeText(a?.icon),
          sectionType: safeText(a?.actionId) || safeText(a?.label),
          isDisabled: disabled,
        };
      });

    return {
      orderDetails,
      title: safeText(header?.title),
      items,
      actionsConfig: actionConfigs,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_TOP_STATUS_CARD: (apiData) => {
    const orderData = (apiData as ApiTopStatusCardData) || {};
    const header = orderData?.header || {};
    const policyProcessing = orderData?.policyProcessing || {};
    const status = safeText(policyProcessing?.status, "");
    const display = policyProcessing?.displayData?.[status] || {};
    const showPolicyUpdateSection = Boolean(
      policyProcessing?.displayData?.[status]
    );
    const actionsList = Array.isArray(orderData?.actionsList)
      ? orderData.actionsList
      : [];
    const downloadAction =
      actionsList.find((a) => getBooleanValue(a?.isVisible)) || actionsList[0];

    const title = safeText(display?.title);
    const subTitle =
      display?.subTitle !== undefined && display?.subTitle !== null
        ? safeText(display.subTitle)
        : undefined;
    const desc = display?.desc ? safeText(display.desc) : undefined;
    const titleColor = display?.titleColor
      ? safeText(display.titleColor)
      : undefined;
    const subTitleColor = display?.subTitleColor
      ? safeText(display.subTitleColor)
      : undefined;
    const descColor = display?.descColor
      ? safeText(display.descColor)
      : undefined;
    const statusUpper = status.toUpperCase();
    const showSpinner =
      statusUpper === "GENERATING_POLICY" || statusUpper === "UPDATING_POLICY";
    const statusIcon = display?.icon ? safeText(display.icon) : undefined;

    return {
      insPlanName: safeText(header?.title),
      insuranceType: undefined,
      orderDetailIcon: safeText(header?.icon, ""),
      policyStatus: {
        title,
        subTitle,
        desc,
        titleColor,
        subTitleColor,
        descColor,
        showSpinner,
        statusIcon,
      },
      downloadAction: downloadAction
        ? {
            label: downloadAction?.label,
            actionId: downloadAction?.actionId,
            icon: downloadAction?.icon
              ? safeText(downloadAction.icon, "")
              : undefined,
            isEnabled: getBooleanValue(downloadAction?.isEnabled),
            isVisible: getBooleanValue(downloadAction?.isVisible),
          }
        : undefined,
      newPolicyUrl: safeText(orderData?.newPolicyUrl, ""),
      showPolicyUpdateSection,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_POLICY_DOWNLOAD: (apiData) => {
    const policy = (apiData as ApiPolicyDownloadData) || {};
    const policyProcessing = policy?.policyProcessing || {};
    const status = safeText(policyProcessing?.status, "");
    const display = policyProcessing?.displayData?.[status] || {};
    const showPolicyUpdateSection =
      policyProcessing?.displayData?.[status] || false;
    const actionsList = Array.isArray(policy?.actionsList)
      ? policy.actionsList
      : [];
    const downloadAction =
      actionsList.find((a) => getBooleanValue(a?.isVisible)) || actionsList[0];

    const title = safeText(display?.title, "");
    const subTitle =
      display?.subTitle !== undefined && display?.subTitle !== null
        ? safeText(display.subTitle, "")
        : undefined;
    const desc = display?.desc ? safeText(display.desc, "") : undefined;
    const titleColor = display?.titleColor
      ? safeText(display.titleColor, "")
      : undefined;
    const subTitleColor = display?.subTitleColor
      ? safeText(display.subTitleColor, "")
      : undefined;
    const descColor = display?.descColor
      ? safeText(display.descColor, "")
      : undefined;
    const statusUpper = status.toUpperCase();
    const showSpinner =
      statusUpper === "GENERATING_POLICY" || statusUpper === "UPDATING_POLICY";
    const statusIcon = display?.icon ? safeText(display.icon, "") : undefined;

    return {
      title,
      subTitle,
      desc,
      titleColor,
      subTitleColor,
      descColor,
      showSpinner,
      statusIcon,
      showPolicyUpdateSection,
      downloadAction: downloadAction
        ? {
            label: downloadAction?.label,
            actionId: downloadAction?.actionId,
            icon: downloadAction?.icon
              ? safeText(downloadAction.icon, "")
              : undefined,
            isEnabled: getBooleanValue(downloadAction?.isEnabled),
            isVisible: getBooleanValue(downloadAction?.isVisible),
          }
        : undefined,
      newPolicyUrl: safeText(policy?.newPolicyUrl, ""),
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_PLAN_BENEFITS: (apiData) => {
    const plan = (apiData as ApiPlanBenefitsData) || {};
    const planBenefits = plan?.planBenefits || {};
    const primaryBenefits = Array.isArray(planBenefits?.primaryBenefits)
      ? planBenefits.primaryBenefits
      : [];

    const moreBenefits = planBenefits?.moreBenefits;
    const moreBenefitsCount =
      moreBenefits && typeof moreBenefits === "object"
        ? Object.values(moreBenefits).reduce(
            (acc: number, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
            0
          )
        : 0;

    const totalBenefits = primaryBenefits.length + moreBenefitsCount;
    const remaining = Math.max(
      0,
      totalBenefits - Math.min(3, primaryBenefits.length)
    );

    return {
      icon: ORDER_DETAIL_V2_ASSETS.PLAN_COVERAGE_ICON,
      title: safeText(plan?.header?.title),
      subTitle: safeText(planBenefits?.header?.title),
      benefits: primaryBenefits.slice(0, 3).map((b) => ({
        title: safeText(b?.title),
        icon: b?.icon || undefined,
        priceNode: b?.subTitle
          ? { text: { value: safeText(b.subTitle) } }
          : undefined,
      })),
      benefitsCTA: remaining ? `${remaining} More Benefits` : "",
      fallbackBenefitIcon: ORDER_DETAIL_V2_ASSETS.TRIP_SECURED_ICON,
      moreBenefits: moreBenefits || undefined,
      footer: planBenefits?.footer || undefined,
    };
  },

  COMPONENT_ORDER_DETAILS_ENDORSEMENT_NUDGE: (apiData) => {
    const nudge = (apiData as ApiEndorsementNudgeData) || {};
    const actions = Array.isArray(nudge?.actionsList) ? nudge.actionsList : [];

    if (!nudge?.title && actions.length === 0) {
      return null;
    }

    return {
      title: nudge?.title || "",
      description: nudge?.description || "",
      icon: nudge?.icon || "",
      actions,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_ENTITY_DETAIL: (apiData, ctx) => {
    const entity = (apiData as ApiEntityDetailData) || {};
    const header = entity?.header || {};
    const actions = Array.isArray(entity?.actionsList)
      ? entity.actionsList
      : [];
    const action: ApiAction =
      actions.find((a) => getBooleanValue(a?.isVisible)) || actions[0] || {};
    const entities = getEntities(ctx);

    const disabled = !getBooleanValue(action?.isEnabled);
    const visible = getBooleanValue(action?.isVisible);
    const entityById = new Map<string, Entity>(entities.map((e) => [e?.id, e]));
    const rawNudge = entity?.nudge;
    const nudge =
      rawNudge?.text || rawNudge?.description
        ? {
            text: rawNudge?.text,
            description: rawNudge?.description,
            backgroundColor: rawNudge?.style?.backgroundColor,
            icon: rawNudge?.icon,
          }
        : null;

    return {
      icon: safeText(header?.icon, ORDER_DETAIL_V2_ASSETS.PRIMARY_CONTACT_ICON),
      title: safeText(header?.title),
      actionLabel: safeText(action?.text),
      isActionDisabled: disabled,
      isActionVisible: visible,
      actionDisabledReason: safeText(action?.disabledReason, ""),
      actionList: actions,
      persuasionText: getBooleanValue(entity?.persuasion?.isVisible)
        ? entity?.persuasion?.text
        : "",
      nudge,
      travellers: (Array.isArray(entity?.displayData)
        ? entity.displayData
        : []
      ).map((t) => {
        const matchedEntity = t?.entityId
          ? entityById.get(t.entityId)
          : undefined;
        const policyUrl =
          matchedEntity?.data?.["policyUrl"] ??
          matchedEntity?.data?.["policyURL"];
        const rawCta = t?.cta;
        const cta = rawCta
          ? {
              ...rawCta,
              isEnabled: getBooleanValue(rawCta.isEnabled),
              isVisible: getBooleanValue(rawCta.isVisible),
            }
          : undefined;
        return {
          id: t?.entityId,
          title: safeText(t?.title),
          desc: t?.desc ? safeText(t.desc, "") : undefined,
          icon: t?.icon ? safeText(t.icon) : undefined,
          detailsMissing: getBooleanValue(t?.detailsMissing),
          cta,
          ...(policyUrl ? { policyUrl: String(policyUrl) } : undefined),
        };
      }),
      formConfig: entity?.form || {},
      modalConfig: entity?.editFlow?.modalConfig,
      entities,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_NOMINEE_DETAIL: (apiData, ctx) => {
    const nominee = (apiData as ApiNomineeDetailData) || {};
    const header = nominee?.header || {};
    const actions = Array.isArray(nominee?.actionsList)
      ? nominee.actionsList
      : [];
    const action: ApiAction =
      actions.find((a) => getBooleanValue(a?.isVisible)) || actions[0] || {};
    const displayData = Array.isArray(nominee?.displayData)
      ? nominee.displayData
      : [];
    const hasNominees = displayData.length > 0;
    const editFlow = nominee?.editFlow || {};
    const form = nominee?.form || {};
    const entities = getEntities(ctx);

    // Use commonActions.addNominee label if nominees exist, otherwise use action label
    const commonActions = nominee?.form?.commonActions || {};
    const actionLabel =
      hasNominees && commonActions?.addNominee?.label
        ? commonActions.addNominee.label
        : safeText(action?.label, "");

    const isActionVisible = getBooleanValue(action?.isVisible);
    const disabled = !getBooleanValue(action?.isEnabled);

    return {
      icon: safeText(header?.icon, ORDER_DETAIL_V2_ASSETS.PRIMARY_CONTACT_ICON),
      title: safeText(header?.title),
      actionLabel,
      isActionDisabled: disabled,
      isActionVisible,
      actionDisabledReason: safeText(action?.disabledReason, ""),
      actionList: actions,
      persuasionText:
        nominee?.persuasion?.isVisible === true
          ? nominee?.persuasion?.text
          : undefined,
      nominees: displayData.map((n) => ({
        entityId: n?.entityId,
        title: safeText(n?.title),
        subTitle: n?.subTitle ? safeText(n.subTitle) : undefined,
        desc: n?.desc ? safeText(n.desc) : undefined,
        icon: n?.icon || null,
      })),
      formConfig: form,
      modalConfig: editFlow?.modalConfig,
      entities: entities,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_CONTACT_DETAIL: (apiData, ctx) => {
    const contact = (apiData as ApiContactDetailData) || {};
    const header = contact?.header || {};
    const displayData = contact?.displayData || {};
    const actions = Array.isArray(contact?.actionsList)
      ? contact.actionsList
      : [];
    const action: ApiAction =
      actions.find((a) => getBooleanValue(a?.isVisible)) || actions[0] || {};

    const entities = getEntities(ctx);
    const primaryTraveller = entities.find((e) => e?.data?.["isPrimary"]);
    const primaryData = primaryTraveller?.data || {};
    const hasAlternateContact = Boolean(
      primaryData?.["alternateContactNo"] || primaryData?.["alternateEmail"]
    );

    const primarySection = {
      title: displayData?.primarySection?.title || action?.label || undefined,
      icon: displayData?.primarySection?.icon || undefined,
      contact: {
        nameNode: { text: { value: safeText(primaryData?.["name"]) } },
        emailNode: {
          text: { value: safeText(primaryData?.["primaryEmail"]) },
        },
        contactNoNode: {
          text: { value: safeText(primaryData?.["primaryContactNo"]) },
        },
      },
    };

    const alternateSection = {
      title: displayData?.alternateSection?.title || undefined,
      icon: displayData?.alternateSection?.icon || undefined,
      isVisible: getBooleanValue(displayData?.alternateSection?.isVisible),
      action: {
        label: action?.label || undefined,
        isEnabled: getBooleanValue(action?.isEnabled),
        isVisible: getBooleanValue(action?.isVisible),
      },
      contact: hasAlternateContact
        ? {
            nameNode: {
              text: { value: safeText(primaryData?.["name"]) },
            },
            emailNode: {
              text: { value: safeText(primaryData?.["alternateEmail"]) },
            },
            contactNoNode: {
              text: {
                value: safeText(primaryData?.["alternateContactNo"]),
              },
            },
          }
        : undefined,
    };

    return {
      sectionTitle: header?.title || undefined,
      sectionIcon: header?.icon || ORDER_DETAIL_V2_ASSETS.PRIMARY_CONTACT_ICON,
      mailIcon: ORDER_DETAIL_V2_ASSETS.MAIL_ICON,
      callIcon: ORDER_DETAIL_V2_ASSETS.CALL_ICON,
      primarySection,
      alternateSection,
      formConfig: contact?.form || null,
      modalConfig: contact?.editFlow?.modalConfig || null,
      primaryEntity: primaryTraveller || null,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_ORDER_ACTIONS: (apiData) => {
    const actionsData = (apiData as ApiOrderActionsData) || {};
    const header = actionsData?.header || {};
    const actionConfigs = actionsData?.actionsConfig || {};
    const actionItemsRaw = Array.isArray(actionsData?.actionsList)
      ? actionsData.actionsList
      : [];

    const items = actionItemsRaw
      .filter((a) => getBooleanValue(a?.isVisible))
      .map((a) => {
        const disabled = !getBooleanValue(a?.isEnabled);
        return {
          title: safeText(a?.label),
          icon: safeText(a?.icon),
          sectionType: safeText(a?.actionId) || safeText(a?.label),
          isDisabled: disabled,
        };
      });

    return {
      title: safeText(header?.title),
      items,
      actionsConfig: actionConfigs,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_PRICE_BREAKUP: (apiData) => {
    const price = (apiData as ApiPriceBreakupData) || {};
    const header = price?.header || {};
    const { breakupList, extraCharges, finalAmount } = extractPriceBreakup(
      price?.priceBreakup || {}
    );

    return {
      title: safeText(header?.title),
      breakupList,
      extraCharges,
      finalAmount,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_PAYMENT_INFO: (apiData, ctx) => {
    const payment = (apiData as ApiPaymentInfoData) || {};
    const header = payment?.header || {};
    const paymentBreakup = Array.isArray(payment?.paymentBreakup)
      ? payment.paymentBreakup
      : [];

    return {
      title: safeText(header?.title),
      info: safeText(header?.subTitle),
      bookingId: safeText(ctx.bookingId, ""),
      paymentBreakup,
      invoiceUrl: safeText(payment?.invoiceUrl, ""),
      headerBackgroundColor:
        header?.style?.backgroundColor || theme.lightBlueBg,
      actionsList: mapActions(payment?.actionsList),
    };
  },

  COMPONENT_ORDER_DETAILS_REFUND_TRACKER: (apiData) => {
    const data = (apiData as ApiOrderDetailsRefundTrackerData) || {};
    return {
      header: data.header,
      amountSummary: data.amountSummary,
      timeline: data.timeline,
      actionsList: Array.isArray(data.actionsList) ? data.actionsList : [],
      refundCalculationBottomSheet: data.refundCalculationBottomSheet,
    };
  },

  COMPONENT_ORDER_DETAILS_PAGE_PRICE_PAYMENT_INFO: (apiData, ctx) => {
    const data = (apiData as ApiPricePaymentInfoData) || {};
    const header = data?.header || {};
    const priceInfo = data?.priceInfo || {};
    const paymentInfo = data?.paymentInfo || {};

    const { breakupList, extraCharges, finalAmount } = extractPriceBreakup(
      priceInfo?.priceBreakup || {}
    );

    const paymentBreakup = Array.isArray(paymentInfo?.paymentBreakup)
      ? paymentInfo.paymentBreakup.map((p) => ({
          sourceText: safeText(p?.sourceText, ""),
          value: safeText(p?.value, ""),
        }))
      : [];

    return {
      title: safeText(header?.title),
      bookingId: safeText(ctx.bookingId, ""),
      breakupList,
      extraCharges,
      finalAmount,
      paymentBreakup,
      invoiceUrl: safeText(paymentInfo?.invoiceUrl, ""),
      headerBackgroundColor:
        paymentInfo?.header?.style?.backgroundColor || "#EAF5FF",
      paymentHeaderTitle: safeText(paymentInfo?.header?.title),
      paymentHeaderValue: safeText(paymentInfo?.header?.subTitle, ""),
      actionsList: mapActions(data?.actionsList),
      paymentActionsList: mapActions(paymentInfo?.actionsList),
    };
  },
};
